/**
 * Client state for the integration/widget auth handshake: the widget token a
 * host EHR launches Heidi with, which product and region that launch names,
 * and how far the account-linking flow has got.
 *
 * One atom per field, not one atom for the group. Twelve files read
 * `widgetToken` and one reads `productName`; a single atom for the group would
 * re-render all twelve every time the widget-auth container syncs a product
 * name off the launch cookie. `integrationAuthAtom` composes the twelve for
 * the six files that destructure more than one field. It is derived, and
 * therefore read-only, so it cannot become a write path back to a single blob.
 *
 * ## Persistence
 *
 * Every field is persisted, and was persisted before — the whole slice sat in
 * the `persist:root` redux-persist blob. Each field now has its OWN frozen
 * storage key under a `{ v, data }` envelope (`@heidi/runtime/storage`
 * `defineVersionedStorage`), which is what makes a later shape change to one
 * field a migration of that field rather than of everything auth-adjacent at
 * once.
 *
 * `runStorageMigrations` below seeds those twelve keys from the legacy blob
 * once per browser profile. It runs at module scope, ABOVE the atom
 * declarations, because `getOnInit: true` reads storage when `atomWithStorage`
 * is called — i.e. during this module's evaluation. It reads the serialized
 * JSON directly and never goes through redux, it is tombstoned so it runs at
 * most once, it never overwrites a target that already has a value, and it
 * leaves the legacy blob in place: deleting that is a separate later release,
 * gated on the desktop fleet minimum version.
 *
 * `getOnInit: true` is required rather than decorative. Two readers are not
 * React — `scribeHTTP`'s `getWidgetToken` and `getRegion()` — and both resolve
 * through `getDefaultStore().get(...)` on paths that can run before anything
 * mounts. Without it they would read the fallback and send an unauthenticated
 * widget request.
 *
 * Five of the writers below have no caller today: nothing outside this module
 * writes `integrationName`, `ehrProvider`, `autoRedirectToWidget`,
 * `hasCheckedLogout` or `hasSignedUserOut`. They are kept because the fields
 * are still READ — seven files read `integrationName` — and older builds
 * persisted values into them that this migration carries forward; dropping
 * the writer would make a field readable and unresettable.
 */
import { logger } from '@/utils/logger/logger';
import {
  defineVersionedStorage,
  encodeVersioned,
  runStorageMigrations,
  type StorageMigration,
  type VersionedStorage,
  type VersionedStorageEvent,
} from '@heidi/runtime/storage';
import { type EHRProvider, type Region } from '@heidi/schema/ml-scribe';
import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export type IntegrationAuthFlowProgress = 'before' | 'in-progress' | 'complete';

export type IntegrationAuthState = {
  widgetToken: string | null;
  // In the webapp `integrationName` holds what the widget calls `productName`;
  // in the widget itself `integrationName` is an `EHRProvider`.
  integrationName?: string;
  widgetRegion?: Region;
  accountLinked: boolean;
  heidiLinkedToOtherEhrAccount: boolean;
  ehrLinkedToOtherHeidiAccount: boolean;
  autoRedirectToWidget?: boolean;
  ehrProvider?: EHRProvider;
  /** `undefined` means we are not in the integration auth flow. */
  integrationAuthFlowProgress?: IntegrationAuthFlowProgress;
  hasCheckedLogout?: boolean;
  hasSignedUserOut?: boolean;
  productName?: string;
};

export type IntegrationAuthField = keyof IntegrationAuthState;

const INITIAL_STATE: IntegrationAuthState = {
  widgetToken: null,
  integrationName: undefined,
  widgetRegion: undefined,
  accountLinked: false,
  heidiLinkedToOtherEhrAccount: false,
  ehrLinkedToOtherHeidiAccount: false,
  autoRedirectToWidget: false,
  ehrProvider: undefined,
  integrationAuthFlowProgress: undefined,
  hasCheckedLogout: false,
  hasSignedUserOut: false,
  productName: undefined,
};

/**
 * One frozen storage key per field: explicit strings, never derived, never
 * renamed. Held in one map rather than twelve constants so the set is
 * enumerable — the migrations below and the reset table further down both
 * walk it, and a new field is a compile error in three places at once.
 */
export const INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD: Record<IntegrationAuthField, string> =
  {
    widgetToken: 'heidi.integrationAuth.widgetToken.v1',
    integrationName: 'heidi.integrationAuth.integrationName.v1',
    widgetRegion: 'heidi.integrationAuth.widgetRegion.v1',
    accountLinked: 'heidi.integrationAuth.accountLinked.v1',
    heidiLinkedToOtherEhrAccount: 'heidi.integrationAuth.heidiLinkedToOtherEhrAccount.v1',
    ehrLinkedToOtherHeidiAccount: 'heidi.integrationAuth.ehrLinkedToOtherHeidiAccount.v1',
    autoRedirectToWidget: 'heidi.integrationAuth.autoRedirectToWidget.v1',
    ehrProvider: 'heidi.integrationAuth.ehrProvider.v1',
    integrationAuthFlowProgress: 'heidi.integrationAuth.integrationAuthFlowProgress.v1',
    hasCheckedLogout: 'heidi.integrationAuth.hasCheckedLogout.v1',
    hasSignedUserOut: 'heidi.integrationAuth.hasSignedUserOut.v1',
    productName: 'heidi.integrationAuth.productName.v1',
  };

const STORAGE_VERSION = 1;

/** The redux-persist blob these twelve values were part of until now. */
const LEGACY_PERSIST_ROOT = 'persist:root';
const LEGACY_SLICE_NAME = 'integrationAuth';

function reportStorageEvent(event: VersionedStorageEvent): void {
  // Counts, keys and versions only — never the stored value, which for
  // `widgetToken` is a credential.
  if (event.type === 'legacy-migration-failed') {
    logger.warn('[integrationAuth] legacy storage migration failed', {
      id: event.id,
      legacyKey: event.legacyKey,
    });
    return;
  }
  if (event.type === 'legacy-skipped') {
    // Warn, not debug: the legacy blob existed and yielded nothing for this
    // key, and the run is tombstoned so it will never be retried. Per key
    // that is often benign (the slice never carried the field); twelve of
    // twelve in one boot is the whole slice gone, which is the failure this
    // event exists to make visible.
    logger.warn('[integrationAuth] legacy storage migration seeded nothing', {
      id: event.id,
      legacyKey: event.legacyKey,
      targetKey: event.targetKey,
    });
    return;
  }
  if (event.type === 'fallback') {
    logger.warn('[integrationAuth] persisted value unreadable, using fallback', {
      key: event.key,
      reason: event.reason,
    });
    return;
  }
  if (event.type === 'write-failed') {
    logger.warn('[integrationAuth] persisted value could not be written', {
      key: event.key,
    });
    return;
  }
  logger.debug('[integrationAuth] persisted value migrated', event);
}

/**
 * Reads `persist:root`'s double-encoded JSON and returns the legacy slice, or
 * `undefined` when the blob carries no `integrationAuth` entry. Throws on an
 * unparseable blob, which the runner records as a failed migration.
 */
function readLegacySlice(rawLegacy: string): Partial<IntegrationAuthState> | undefined {
  const parsed = JSON.parse(rawLegacy) as Record<string, string | undefined>;
  const slice = parsed[LEGACY_SLICE_NAME];
  if (typeof slice !== 'string') return undefined;
  return JSON.parse(slice) as Partial<IntegrationAuthState>;
}

/**
 * One migration per key. A field the legacy blob does not carry seeds
 * nothing, so the target key stays absent and its fallback applies — which is
 * the value the slice resolved for a field its `initialState` never declared.
 */
export const INTEGRATION_AUTH_LEGACY_MIGRATIONS: readonly StorageMigration[] = (
  Object.keys(INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD) as IntegrationAuthField[]
).map((field) => ({
  id: `integrationAuth.${field}.from-persist-root.v1`,
  legacyKey: LEGACY_PERSIST_ROOT,
  targetKey: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD[field],
  transform: (rawLegacy: string): string | null => {
    const legacy = readLegacySlice(rawLegacy);
    if (!legacy || !(field in legacy)) return null;
    const value = legacy[field];
    if (value === undefined) return null;
    return encodeVersioned(STORAGE_VERSION, value);
  },
}));

runStorageMigrations(INTEGRATION_AUTH_LEGACY_MIGRATIONS, reportStorageEvent);

/**
 * `undefined` is a real value for the optional fields, and JSON has no
 * representation for it: `JSON.stringify({ v: 1, data: undefined })` drops
 * `data` entirely and the decoder would read every subsequent load as
 * corrupt. Writing `undefined` removes the key instead, so the next read
 * falls back to `undefined` — which is also how the redux-persist blob
 * behaved, since it never serialized an undefined field either.
 */
function withUndefinedAsAbsent<T>(storage: VersionedStorage<T>): VersionedStorage<T> {
  const write = (value: T): void => {
    if (value === undefined) {
      storage.clear();
      return;
    }
    storage.write(value);
  };

  return {
    ...storage,
    write,
    adapter: { ...storage.adapter, setItem: (_key: string, value: T) => write(value) },
  };
}

/**
 * The one field with a same-version shape check. Every other field here is
 * read into a boolean/string the UI tolerates being odd; this one is
 * concatenated into an `Authorization` header, so a well-formed envelope
 * carrying a non-string payload — the shape a legacy transform copying the
 * redux value verbatim would produce — sends `Bearer [object Object]` and
 * earns a 401 that looks like an auth bug rather than a storage one. Failing
 * the check falls back to `null`, which is the unauthenticated path the
 * callers already handle.
 */
function isWidgetToken(data: unknown): data is string | null {
  return data === null || typeof data === 'string';
}

const widgetTokenStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | null>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.widgetToken,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.widgetToken,
    validate: isWidgetToken,
    onEvent: reportStorageEvent,
  })
);
export const widgetTokenAtom = atomWithStorage<string | null>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.widgetToken,
  INITIAL_STATE.widgetToken,
  widgetTokenStorage.adapter,
  { getOnInit: true }
);

const integrationNameStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.integrationName,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.integrationName,
    onEvent: reportStorageEvent,
  })
);
export const integrationNameAtom = atomWithStorage<string | undefined>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.integrationName,
  INITIAL_STATE.integrationName,
  integrationNameStorage.adapter,
  { getOnInit: true }
);

const widgetRegionStorage = withUndefinedAsAbsent(
  defineVersionedStorage<Region | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.widgetRegion,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.widgetRegion,
    onEvent: reportStorageEvent,
  })
);
export const widgetRegionAtom = atomWithStorage<Region | undefined>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.widgetRegion,
  INITIAL_STATE.widgetRegion,
  widgetRegionStorage.adapter,
  { getOnInit: true }
);

const accountLinkedStorage = withUndefinedAsAbsent(
  defineVersionedStorage<boolean>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.accountLinked,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.accountLinked,
    onEvent: reportStorageEvent,
  })
);
export const accountLinkedAtom = atomWithStorage<boolean>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.accountLinked,
  INITIAL_STATE.accountLinked,
  accountLinkedStorage.adapter,
  { getOnInit: true }
);

const heidiLinkedToOtherEhrAccountStorage = withUndefinedAsAbsent(
  defineVersionedStorage<boolean>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.heidiLinkedToOtherEhrAccount,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.heidiLinkedToOtherEhrAccount,
    onEvent: reportStorageEvent,
  })
);
export const heidiLinkedToOtherEhrAccountAtom = atomWithStorage<boolean>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.heidiLinkedToOtherEhrAccount,
  INITIAL_STATE.heidiLinkedToOtherEhrAccount,
  heidiLinkedToOtherEhrAccountStorage.adapter,
  { getOnInit: true }
);

const ehrLinkedToOtherHeidiAccountStorage = withUndefinedAsAbsent(
  defineVersionedStorage<boolean>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.ehrLinkedToOtherHeidiAccount,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.ehrLinkedToOtherHeidiAccount,
    onEvent: reportStorageEvent,
  })
);
export const ehrLinkedToOtherHeidiAccountAtom = atomWithStorage<boolean>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.ehrLinkedToOtherHeidiAccount,
  INITIAL_STATE.ehrLinkedToOtherHeidiAccount,
  ehrLinkedToOtherHeidiAccountStorage.adapter,
  { getOnInit: true }
);

const autoRedirectToWidgetStorage = withUndefinedAsAbsent(
  defineVersionedStorage<boolean | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.autoRedirectToWidget,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.autoRedirectToWidget,
    onEvent: reportStorageEvent,
  })
);
export const autoRedirectToWidgetAtom = atomWithStorage<boolean | undefined>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.autoRedirectToWidget,
  INITIAL_STATE.autoRedirectToWidget,
  autoRedirectToWidgetStorage.adapter,
  { getOnInit: true }
);

const ehrProviderStorage = withUndefinedAsAbsent(
  defineVersionedStorage<EHRProvider | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.ehrProvider,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.ehrProvider,
    onEvent: reportStorageEvent,
  })
);
export const ehrProviderAtom = atomWithStorage<EHRProvider | undefined>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.ehrProvider,
  INITIAL_STATE.ehrProvider,
  ehrProviderStorage.adapter,
  { getOnInit: true }
);

const integrationAuthFlowProgressStorage = withUndefinedAsAbsent(
  defineVersionedStorage<IntegrationAuthFlowProgress | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.integrationAuthFlowProgress,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.integrationAuthFlowProgress,
    onEvent: reportStorageEvent,
  })
);
export const integrationAuthFlowProgressAtom = atomWithStorage<
  IntegrationAuthFlowProgress | undefined
>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.integrationAuthFlowProgress,
  INITIAL_STATE.integrationAuthFlowProgress,
  integrationAuthFlowProgressStorage.adapter,
  { getOnInit: true }
);

const hasCheckedLogoutStorage = withUndefinedAsAbsent(
  defineVersionedStorage<boolean | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.hasCheckedLogout,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.hasCheckedLogout,
    onEvent: reportStorageEvent,
  })
);
export const hasCheckedLogoutAtom = atomWithStorage<boolean | undefined>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.hasCheckedLogout,
  INITIAL_STATE.hasCheckedLogout,
  hasCheckedLogoutStorage.adapter,
  { getOnInit: true }
);

const hasSignedUserOutStorage = withUndefinedAsAbsent(
  defineVersionedStorage<boolean | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.hasSignedUserOut,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.hasSignedUserOut,
    onEvent: reportStorageEvent,
  })
);
export const hasSignedUserOutAtom = atomWithStorage<boolean | undefined>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.hasSignedUserOut,
  INITIAL_STATE.hasSignedUserOut,
  hasSignedUserOutStorage.adapter,
  { getOnInit: true }
);

const productNameStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | undefined>({
    key: INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.productName,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.productName,
    onEvent: reportStorageEvent,
  })
);
export const productNameAtom = atomWithStorage<string | undefined>(
  INTEGRATION_AUTH_STORAGE_KEY_BY_FIELD.productName,
  INITIAL_STATE.productName,
  productNameStorage.adapter,
  { getOnInit: true }
);

/**
 * The envelope behind each atom, keyed by field. Exposed because the repo's
 * jest `jotai/utils` mock replaces `atomWithStorage` with an implementation
 * that ignores the storage argument, so the versioned contract is only
 * checkable here; `read`/`write` are also the door a non-atom caller uses.
 */
export const integrationAuthStorage = {
  widgetToken: widgetTokenStorage,
  integrationName: integrationNameStorage,
  widgetRegion: widgetRegionStorage,
  accountLinked: accountLinkedStorage,
  heidiLinkedToOtherEhrAccount: heidiLinkedToOtherEhrAccountStorage,
  ehrLinkedToOtherHeidiAccount: ehrLinkedToOtherHeidiAccountStorage,
  autoRedirectToWidget: autoRedirectToWidgetStorage,
  ehrProvider: ehrProviderStorage,
  integrationAuthFlowProgress: integrationAuthFlowProgressStorage,
  hasCheckedLogout: hasCheckedLogoutStorage,
  hasSignedUserOut: hasSignedUserOutStorage,
  productName: productNameStorage,
};

/** The whole shape, for the readers that destructure more than one field. */
export const integrationAuthAtom = atom<IntegrationAuthState>((get) => ({
  widgetToken: get(widgetTokenAtom),
  integrationName: get(integrationNameAtom),
  widgetRegion: get(widgetRegionAtom),
  accountLinked: get(accountLinkedAtom),
  heidiLinkedToOtherEhrAccount: get(heidiLinkedToOtherEhrAccountAtom),
  ehrLinkedToOtherHeidiAccount: get(ehrLinkedToOtherHeidiAccountAtom),
  autoRedirectToWidget: get(autoRedirectToWidgetAtom),
  ehrProvider: get(ehrProviderAtom),
  integrationAuthFlowProgress: get(integrationAuthFlowProgressAtom),
  hasCheckedLogout: get(hasCheckedLogoutAtom),
  hasSignedUserOut: get(hasSignedUserOutAtom),
  productName: get(productNameAtom),
}));

export function setWidgetToken(widgetToken: string | null): void {
  getDefaultStore().set(widgetTokenAtom, widgetToken);
}

export function setWidgetRegion(widgetRegion: Region): void {
  getDefaultStore().set(widgetRegionAtom, widgetRegion);
}

export function setIntegrationName(integrationName?: string): void {
  getDefaultStore().set(integrationNameAtom, integrationName);
}

export function setEhrProvider(ehrProvider?: EHRProvider): void {
  getDefaultStore().set(ehrProviderAtom, ehrProvider);
}

export function setAccountLinked(accountLinked: boolean): void {
  getDefaultStore().set(accountLinkedAtom, accountLinked);
}

export function setHeidiLinkedToOtherEhrAccount(
  heidiLinkedToOtherEhrAccount: boolean
): void {
  getDefaultStore().set(heidiLinkedToOtherEhrAccountAtom, heidiLinkedToOtherEhrAccount);
}

export function setEhrLinkedToOtherHeidiAccount(
  ehrLinkedToOtherHeidiAccount: boolean
): void {
  getDefaultStore().set(ehrLinkedToOtherHeidiAccountAtom, ehrLinkedToOtherHeidiAccount);
}

export function setProductName(productName?: string): void {
  getDefaultStore().set(productNameAtom, productName);
}

export function setAutoRedirectToWidget(autoRedirectToWidget?: boolean): void {
  getDefaultStore().set(autoRedirectToWidgetAtom, autoRedirectToWidget);
}

export function setHasCheckedLogOut(hasCheckedLogout?: boolean): void {
  getDefaultStore().set(hasCheckedLogoutAtom, hasCheckedLogout);
}

export function setHasSignedUserOut(hasSignedUserOut?: boolean): void {
  getDefaultStore().set(hasSignedUserOutAtom, hasSignedUserOut);
}

export function setIntegrationAuthFlowProgress(
  integrationAuthFlowProgress?: IntegrationAuthFlowProgress
): void {
  getDefaultStore().set(integrationAuthFlowProgressAtom, integrationAuthFlowProgress);
}

type Store = ReturnType<typeof getDefaultStore>;

/**
 * One reset per field, keyed by field so a new field is a compile error here
 * rather than a value that silently survives a clear.
 */
const RESET_BY_FIELD: Record<IntegrationAuthField, (store: Store) => void> = {
  widgetToken: (store) => store.set(widgetTokenAtom, INITIAL_STATE.widgetToken),
  integrationName: (store) =>
    store.set(integrationNameAtom, INITIAL_STATE.integrationName),
  widgetRegion: (store) => store.set(widgetRegionAtom, INITIAL_STATE.widgetRegion),
  accountLinked: (store) => store.set(accountLinkedAtom, INITIAL_STATE.accountLinked),
  heidiLinkedToOtherEhrAccount: (store) =>
    store.set(
      heidiLinkedToOtherEhrAccountAtom,
      INITIAL_STATE.heidiLinkedToOtherEhrAccount
    ),
  ehrLinkedToOtherHeidiAccount: (store) =>
    store.set(
      ehrLinkedToOtherHeidiAccountAtom,
      INITIAL_STATE.ehrLinkedToOtherHeidiAccount
    ),
  autoRedirectToWidget: (store) =>
    store.set(autoRedirectToWidgetAtom, INITIAL_STATE.autoRedirectToWidget),
  ehrProvider: (store) => store.set(ehrProviderAtom, INITIAL_STATE.ehrProvider),
  integrationAuthFlowProgress: (store) =>
    store.set(integrationAuthFlowProgressAtom, INITIAL_STATE.integrationAuthFlowProgress),
  hasCheckedLogout: (store) =>
    store.set(hasCheckedLogoutAtom, INITIAL_STATE.hasCheckedLogout),
  hasSignedUserOut: (store) =>
    store.set(hasSignedUserOutAtom, INITIAL_STATE.hasSignedUserOut),
  productName: (store) => store.set(productNameAtom, INITIAL_STATE.productName),
};

const ALL_FIELDS = Object.keys(RESET_BY_FIELD) as IntegrationAuthField[];

/** Every field back to its initial value, unconditionally. */
export function clearIntegrationAuth(): void {
  const store = getDefaultStore();
  ALL_FIELDS.forEach((field) => RESET_BY_FIELD[field](store));
}

/**
 * Every field back to its initial value EXCEPT the ones named, which keep
 * whatever they currently hold — including a falsy or `undefined` one. This
 * mirrors the slice-wide reducer it replaces, which rebuilt the object from
 * `initialState` and then wrote the current value of each excluded field back
 * over it.
 */
export function clearIntegrationAuthWithExclusions(
  exclusions: readonly IntegrationAuthField[]
): void {
  const store = getDefaultStore();
  const kept = new Set<IntegrationAuthField>(exclusions);
  ALL_FIELDS.forEach((field) => {
    if (kept.has(field)) return;
    RESET_BY_FIELD[field](store);
  });
}

/**
 * The fields a full sign-out keeps, so a host-EHR launch survives it and the
 * widget can sign the next user in against the same launch. Everything else
 * is reset by `clearIntegrationAuthOnLogout`.
 */
export const INTEGRATION_AUTH_KEPT_ACROSS_LOGOUT: readonly IntegrationAuthField[] = [
  'widgetToken',
  'integrationName',
  'widgetRegion',
  'ehrProvider',
  'integrationAuthFlowProgress',
  'productName',
];

/** The logout reset, as one call, so the kept-field list has a single home. */
export function clearIntegrationAuthOnLogout(): void {
  clearIntegrationAuthWithExclusions(INTEGRATION_AUTH_KEPT_ACROSS_LOGOUT);
}

/**
 * The widget token the OpenAPI HTTP client authenticates with. `scribeHTTP`
 * resolves it per request, outside React and often before anything has
 * mounted, so it takes the value through this getter.
 */
export function getWidgetToken(): string | null {
  return getDefaultStore().get(widgetTokenAtom);
}

/**
 * The region a widget launch named. `getRegion()` is a plain function called
 * from non-React paths, so it reads through this getter; `useRegion()` reads
 * the atom directly.
 */
export function getWidgetRegion(): Region | undefined {
  return getDefaultStore().get(widgetRegionAtom);
}
